import * as React from 'react';
import classNames from 'classnames';
import * as _ from 'lodash';
import { withRouter } from 'react-router';
import Avatar from '../../../components/Avatar';
import Tooltip from '../../../components/Tooltip';
import { __, setTableParams } from '../../../core/utils';
import { deleteRelatedTask } from '../../../redux/actions/table/all_tasks';
import ArrowDoubleRightSmallIcon from '../../../assets/images/arrow-double-right-small-15x15.svg';
import CloseMiddleIcon from '../../../assets/images/close-middle-15x15.svg';
import styles from '../../../styles/flyouts/tasks/Relations.scss';
import { TaskStatus, TaskTypeIcon } from './TaskRelationVisuals';
import { confirmRelationRemoval, openRelationTask } from './relationHelpers';

const priorityConfig = {
  low: {
    tooltip: 'tasks.all-tasks.flyout.related-tasks.low',
    label: 'table.columns.task-priority-low-short',
  },
  normal: {
    tooltip: 'tasks.all-tasks.flyout.related-tasks.normal',
    label: 'table.columns.task-priority-normal-short',
  },
  high: {
    tooltip: 'tasks.all-tasks.flyout.related-tasks.high',
    label: 'table.columns.task-priority-high-short',
  },
  urgent: {
    tooltip: 'tasks.all-tasks.flyout.related-tasks.urgent',
    label: 'table.columns.task-priority-urgent-short',
  },
};

const TaskPriority = ({ priority }) => {
  const config = priorityConfig[priority];

  if (!config) {
    return null;
  }

  return (
    <Tooltip text={__(config.tooltip)}>
      <span className={classNames(styles.balloon, styles[priority])}>
        {__(config.label)}
      </span>
    </Tooltip>
  );
};

const TaskVisibility = ({ visibility }) => {
  const value = _.toString(visibility);
  const config =
    value === '2'
      ? {
          label: 'co',
          style: 'company',
          tooltip: __('tasks.visibility.company'),
        }
      : value === '1'
      ? {
          label: __('tasks.flyout.related-tasks.pu'),
          style: 'public',
          tooltip: 'Public',
        }
      : {
          label: __('tasks.flyout.related-tasks.pr'),
          style: 'private',
          tooltip: 'Private',
        };

  return (
    <Tooltip text={config.tooltip}>
      <span className={classNames(styles.balloon, styles[config.style])}>
        {config.label}
      </span>
    </Tooltip>
  );
};

const RelatedTasks = ({ data, match }) => {
  const removeRelatedTask = (event, relatedTaskId) => {
    event.stopPropagation();

    confirmRelationRemoval({
      description: __(
        'tasks.all-tasks.flyout.related-tasks.are-you-sure-delete-related-task'
      ),
      notification: __(
        'tasks.all-tasks.flyout.related-tasks.related-task-deleted'
      ),
      remove: () =>
        deleteRelatedTask(data.id, {
          related_task_id: relatedTaskId,
        }),
      taskId: data.id,
      match,
    });
  };

  return (
    <>
      <div className={styles.dividerLine} />
      <div
        className={classNames(
          styles.flex,
          styles.sectionHeader,
          styles.relatedSectionHeader
        )}
      >
        <div className={styles.f1}>
          <label className={styles.label}>
            {__('tasks.flyout.related_tasks')}
          </label>
        </div>
        <div className={styles.headerActions}>
          <button
            onClick={() =>
              setTableParams('all_tasks', {
                add_related_task_wizard: data.id,
              })
            }
          >
            {__('button.add-related-task')}
          </button>
        </div>
      </div>

      {_.map(data.related_tasks, (task, index) => (
        <div
          className={classNames(styles.row, styles.relatedRow)}
          key={task.id || index}
        >
          <TaskTypeIcon type={task.type} />
          <div
            onClick={() => openRelationTask(task, match)}
            className={classNames(
              styles.link,
              task.can_view ? styles.clickable : styles.disabled
            )}
          >
            {!task.can_view && (
              <Tooltip
                text={__(
                  'tasks.all-tasks.flyout.related-tasks.tooltip.private-related-task.no-access'
                )}
              >
                <span>
                  {'T-' + task.identifier} &middot; {task.title}
                </span>
              </Tooltip>
            )}
            {task.can_view && (
              <span>
                {'T-' + task.identifier} &middot; {task.title}
              </span>
            )}{' '}
            {task.can_view && <ArrowDoubleRightSmallIcon />}
          </div>

          {!_.isEmpty(task.assignee) && (
            <>
              <div className={styles.assignee}>
                {_.size(task.assignee) === 1 &&
                  _.map(_.take(task.assignee, 1), (user, userIndex) => (
                    <Avatar
                      key={userIndex}
                      avatar_url={user.avatar_url}
                      name={user.fullname}
                      active={user.active}
                    />
                  ))}
                {_.size(task.assignee) > 1 && (
                  <Tooltip text={_.map(task.assignee, 'fullname').join('\n')}>
                    <div className={styles.more}>
                      <span>{_.size(task.assignee)}</span>
                    </div>
                  </Tooltip>
                )}
              </div>
              <div className={styles.separator} />
            </>
          )}
          <TaskStatus status={task.status} />
          <div className={styles.separator} />
          <TaskPriority priority={task.priority} />
          <div className={styles.separator} />
          <TaskVisibility visibility={task.visibility} />
          <div className={styles.actions}>
            <Tooltip
              text={__(
                'tasks.all-tasks.flyout.related-tasks.delete-related-task'
              )}
            >
              <CloseMiddleIcon
                onClick={(event) => removeRelatedTask(event, task.id)}
              />
            </Tooltip>
          </div>
        </div>
      ))}
    </>
  );
};

export default withRouter(RelatedTasks);
